<template>
  <div class="picking-assignment">
    <v-container fluid>
      <!-- Header -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="d-flex justify-space-between align-center mb-4">
            <h1 class="text-h4 font-weight-bold">Asignación de Picking</h1>
            <v-btn color="primary" @click="loadData" prepend-icon="mdi-refresh">
              Actualizar
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Estadísticas -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="warning" size="40" class="mr-3">mdi-clipboard-list</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ pendingLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">Listas Pendientes</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="info" size="40" class="mr-3">mdi-account-hard-hat</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ operators.length }}</div>
                  <div class="text-subtitle-2 text-grey">Operarios Disponibles</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="success" size="40" class="mr-3">mdi-check-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ assignedLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">Listas Asignadas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card">
            <v-card-text>
              <div class="d-flex align-center">
                <v-icon color="primary" size="40" class="mr-3">mdi-play-circle</v-icon>
                <div>
                  <div class="text-h4 font-weight-bold">{{ inProgressLists.length }}</div>
                  <div class="text-subtitle-2 text-grey">En Progreso</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <!-- Listas de Picking Pendientes -->
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <span class="text-h6">Listas Pendientes de Asignación</span>
            </v-card-title>
            
            <v-card-text>
              <v-data-table
                :headers="pendingHeaders"
                :items="pendingLists"
                :loading="loading"
                class="picking-table"
                item-key="id"
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:item.priority="{ item }">
                  <v-chip :color="getPriorityColor(item.priority)" size="small">
                    {{ getPriorityText(item.priority) }}
                  </v-chip>
                </template>

                <template v-slot:item.estimated_time="{ item }">
                  {{ item.estimated_time }} min
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    color="primary"
                    size="small"
                    @click="showAssignDialog(item)"
                    prepend-icon="mdi-account-plus"
                  >
                    Asignar
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Operarios y sus Asignaciones -->
        <v-col cols="12" md="6">
          <v-card>
            <v-card-title>
              <span class="text-h6">Operarios y Asignaciones</span>
            </v-card-title>
            
            <v-card-text>
              <v-expansion-panels>
                <v-expansion-panel v-for="operator in operators" :key="operator.id">
                  <v-expansion-panel-title>
                    <div class="d-flex align-center justify-space-between w-100">
                      <div class="d-flex align-center">
                        <v-avatar color="primary" size="30" class="mr-3">
                          <span class="text-white">{{ operator.username.charAt(0).toUpperCase() }}</span>
                        </v-avatar>
                        <div>
                          <div class="font-weight-bold">{{ operator.username }}</div>
                          <div class="text-caption text-grey">{{ operator.email }}</div>
                        </div>
                      </div>
                      <v-chip 
                        :color="getOperatorStatusColor(operator)" 
                        size="small"
                      >
                        {{ getOperatorTaskCount(operator.id) }} tareas
                      </v-chip>
                    </div>
                  </v-expansion-panel-title>
                  
                  <v-expansion-panel-text>
                    <div v-if="getOperatorTasks(operator.id).length === 0" class="text-center text-grey py-4">
                      Sin tareas asignadas
                    </div>
                    <div v-else>
                      <v-list density="compact">
                        <v-list-item 
                          v-for="task in getOperatorTasks(operator.id)" 
                          :key="task.id"
                          class="task-item"
                        >
                          <template v-slot:prepend>
                            <v-icon :color="getStatusColor(task.status)">
                              {{ getStatusIcon(task.status) }}
                            </v-icon>
                          </template>
                          
                          <v-list-item-title>{{ task.list_number }}</v-list-item-title>
                          <v-list-item-subtitle>
                            Orden: {{ task.order_number }} | {{ task.estimated_time }} min
                          </v-list-item-subtitle>
                          
                          <template v-slot:append>
                            <v-chip :color="getStatusColor(task.status)" size="small">
                              {{ getStatusText(task.status) }}
                            </v-chip>
                          </template>
                        </v-list-item>
                      </v-list>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialog de Asignación -->
    <v-dialog v-model="showAssignmentDialog" max-width="500px" persistent>
      <v-card v-if="selectedList">
        <v-card-title>
          <span class="text-h5">Asignar Lista de Picking</span>
        </v-card-title>
        
        <v-card-text>
          <div class="mb-4">
            <h3>{{ selectedList.list_number }}</h3>
            <p class="text-grey">Orden: {{ selectedList.order_number }}</p>
            <p class="text-grey">Tiempo estimado: {{ selectedList.estimated_time }} minutos</p>
            <v-chip :color="getPriorityColor(selectedList.priority)" size="small">
              {{ getPriorityText(selectedList.priority) }}
            </v-chip>
          </div>

          <v-divider class="my-4"></v-divider>

          <v-select
            v-model="selectedOperator"
            :items="operatorOptions"
            label="Seleccionar Operario *"
            item-title="text"
            item-value="value"
            :rules="[v => !!v || 'Debe seleccionar un operario']"
            required
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-avatar color="primary" size="30">
                    <span class="text-white">{{ item.raw.username.charAt(0).toUpperCase() }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>{{ item.raw.username }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.raw.email }} | {{ getOperatorTaskCount(item.raw.id) }} tareas
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="cancelAssignment">Cancelar</v-btn>
          <v-btn 
            color="primary" 
            @click="confirmAssignment"
            :disabled="!selectedOperator"
            :loading="assigning"
          >
            Asignar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'PickingAssignment',
  data() {
    return {
      loading: false,
      assigning: false,
      
      // Datos
      pickingLists: [],
      operators: [],
      
      // Dialog
      showAssignmentDialog: false,
      selectedList: null,
      selectedOperator: null,
      
      // Headers de tabla
      pendingHeaders: [
        { title: 'Lista', key: 'list_number', sortable: true },
        { title: 'Orden', key: 'order_number', sortable: true },
        { title: 'Prioridad', key: 'priority', sortable: true },
        { title: 'Tiempo Est.', key: 'estimated_time', sortable: true },
        { title: 'Acciones', key: 'actions', sortable: false }
      ],
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  
  computed: {
    pendingLists() {
      return this.pickingLists.filter(list => list.status === 'pending')
    },
    
    assignedLists() {
      return this.pickingLists.filter(list => list.status === 'assigned')
    },
    
    inProgressLists() {
      return this.pickingLists.filter(list => list.status === 'in_progress')
    },
    
    operatorOptions() {
      return this.operators.map(op => ({
        text: `${op.username} (${this.getOperatorTaskCount(op.id)} tareas)`,
        value: op.id,
        username: op.username,
        email: op.email,
        id: op.id
      }))
    }
  },
  
  async mounted() {
    await this.loadData()
  },
  
  methods: {
    async loadData() {
      this.loading = true
      try {
        // Cargar listas de picking y operarios en paralelo
        const [listsResponse, operatorsResponse] = await Promise.all([
          api.getPickingLists(),
          api.getOperators()
        ])
        
        this.pickingLists = listsResponse.data
        this.operators = operatorsResponse.data
      } catch (error) {
        this.showSnackbar('Error al cargar datos', 'error')
        console.error('Error loading data:', error)
      } finally {
        this.loading = false
      }
    },
    
    showAssignDialog(list) {
      this.selectedList = list
      this.selectedOperator = null
      this.showAssignmentDialog = true
    },
    
    cancelAssignment() {
      this.showAssignmentDialog = false
      this.selectedList = null
      this.selectedOperator = null
    },
    
    async confirmAssignment() {
      if (!this.selectedOperator || !this.selectedList) return
      
      this.assigning = true
      try {
        await api.assignPickingList(this.selectedList.id, {
          user_id: this.selectedOperator
        })
        
        this.showSnackbar('Lista asignada exitosamente')
        this.cancelAssignment()
        await this.loadData()
      } catch (error) {
        this.showSnackbar('Error al asignar lista', 'error')
        console.error('Error assigning list:', error)
      } finally {
        this.assigning = false
      }
    },
    
    getOperatorTasks(operatorId) {
      return this.pickingLists.filter(list => 
        list.assigned_user_id === operatorId && 
        ['assigned', 'in_progress'].includes(list.status)
      )
    },
    
    getOperatorTaskCount(operatorId) {
      return this.getOperatorTasks(operatorId).length
    },
    
    getOperatorStatusColor(operator) {
      const taskCount = this.getOperatorTaskCount(operator.id)
      if (taskCount === 0) return 'success'
      if (taskCount <= 2) return 'warning'
      return 'error'
    },
    
    // Utilidades de estado
    getStatusColor(status) {
      const colors = {
        pending: 'grey',
        assigned: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    },
    
    getStatusText(status) {
      const texts = {
        pending: 'Pendiente',
        assigned: 'Asignado',
        in_progress: 'En Progreso',
        completed: 'Completado',
        cancelled: 'Cancelado'
      }
      return texts[status] || status
    },
    
    getStatusIcon(status) {
      const icons = {
        pending: 'mdi-clock-outline',
        assigned: 'mdi-account-check',
        in_progress: 'mdi-play-circle',
        completed: 'mdi-check-circle',
        cancelled: 'mdi-close-circle'
      }
      return icons[status] || 'mdi-help-circle'
    },
    
    getPriorityColor(priority) {
      const colors = {
        1: 'error',
        2: 'warning',
        3: 'info',
        4: 'success',
        5: 'grey'
      }
      return colors[priority] || 'grey'
    },
    
    getPriorityText(priority) {
      const texts = {
        1: 'Muy Alta',
        2: 'Alta',
        3: 'Media',
        4: 'Baja',
        5: 'Muy Baja'
      }
      return texts[priority] || `Prioridad ${priority}`
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color
      }
    }
  }
}
</script>

<style scoped>
.picking-assignment {
  padding: 20px;
}

.stats-card {
  height: 100%;
}

.picking-table {
  background-color: white;
}

.picking-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.picking-table >>> th {
  background-color: #f8f9fa !important;
  font-weight: bold !important;
  border-bottom: 2px solid #dee2e6 !important;
}

.picking-table >>> td {
  border-bottom: 1px solid #dee2e6 !important;
  padding: 12px 16px !important;
}

.picking-table >>> tr:hover {
  background-color: #f8f9fa !important;
}

.task-item {
  border-left: 3px solid transparent;
  margin-bottom: 8px;
  border-radius: 4px;
}

.task-item:hover {
  background-color: #f5f5f5;
  border-left-color: #1976d2;
}
</style> 